import { useTerminalsStateRE } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { AlertLine } from "@/components/AlertLine/AlertLine";
import { StyledSelect } from "@/components/StyledSelect";
import { Button } from "@/components/button/index";
import { useTrans } from "@/translations";

import { FC, useEffect, useState } from "react";

import { CustomerWithDetailsAndCode } from "server/coreApi/customers/types.js";

interface CommunicationSectionProps {
  customer: CustomerWithDetailsAndCode;
  type: "mobile" | "tablet";
}

export const CommunicationSection: FC<CommunicationSectionProps> = ({
  customer,
  type,
}) => {
  const { t, language } = useTrans();
  const { setGlobalAlert } = useGlobalAlert();
  const { languages } = useCommonEntitiesStore();

  const { currentTerminal } = useTerminalsStateRE();
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [pickedLanguage, setPickedLanguage] = useState(language);

  const {
    mutate: sendWelcomeEmail,
    data: result,
    isPending: isLoading,
    isSuccess,
    isError: isMutationError,
  } = trpc.notifications.sendWelcomeEmail.useMutation();

  useEffect(() => {
    setIsEmailSent(false);
  }, [customer]);

  const onButtonClick = () => {
    sendWelcomeEmail({
      customerId: customer.id,
      terminalId: currentTerminal.id,
      type,
      language: pickedLanguage,
    });
    setIsEmailSent(true);
  };

  const close = () => setIsEmailSent(false);

  const isError =
    isEmailSent && (isMutationError || (isSuccess && result?.status > 0));

  useEffect(() => {
    if (isEmailSent && !isLoading && !isError) {
      setGlobalAlert({
        type: "success",
        message: "hints:welcomeEmailSent",
      });
    }
  }, [isLoading, isError, setGlobalAlert, isEmailSent]);

  const languageOptions = languages?.map((lang) => ({
    label: lang.name,
    value: lang.code,
  }));

  return (
    <div className="mb-6">
      <p className="font-dm-sans text-base font-medium text-black">
        {t(`hints:onboardingEmailTitle_${type}`)}
      </p>
      <p className="font-dm-sans mb-5 text-sm text-black">
        {t(`hints:onboardingEmailDescription_${type}`)}
      </p>
      {isError ? (
        <div className="text-carrot-red flex h-[40px] w-full font-bold">
          <AlertLine
            type="error"
            message="errors:errorSendingEmail"
            close={close}
            isStatic
          />
        </div>
      ) : (
        <div className="flex">
          {!customer.companyCode && (
            <div className="text-carrot-red mb-4 w-full font-bold">
              <AlertLine
                type="error"
                message="errors:companyCodeMissing"
                canClose={false}
                isStatic
              />
            </div>
          )}
          <div className="w-2/3">
            <label className="text-sm font-medium select-none">
              {t("configLabels:selectEmailLanguage")}
            </label>
            <StyledSelect
              className="mt-1 mb-4"
              options={languageOptions}
              value={languageOptions?.find((el) => el.value === pickedLanguage)}
              onChange={(option) => setPickedLanguage(option.value)}
            />
          </div>
          <Button
            loading={isLoading}
            disabled={!customer.companyCode || !customer.contactEmail}
            label={"actions:sendWelcomeEmail"}
            color="secondary"
            className="float-right mt-7 ml-3"
            onClick={onButtonClick}
          />
        </div>
      )}
    </div>
  );
};
